<!-- eslint-disable vue/no-template-shadow -->
<template>
  <div class="spk-test">
    <APageHead title="AI 자율대화 테스트" sub="AI와 실시간 대화를 나누며 자발적 대화 능력을 테스트합니다.">
      <template #actions>
        <ABtn v-if="messages.length > 0" kind="ghost" icon="refresh" @click="resetConversation">초기화</ABtn>
      </template>
    </APageHead>

    <!-- 설정 카드 -->
    <ACard v-if="!conversationStarted">
      <h5 class="spk-test__sub-h">
        <i class="material-icons-round">settings</i>
        대화 설정
      </h5>
      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">교재 바코드 (Barcode)</label>
          <input v-model="config.barcode" class="p-input" placeholder="9781635190151" />
        </div>
        <div class="p-field">
          <label class="p-field__label">챕터 번호 (Chapter)</label>
          <input v-model.number="config.chapterNumber" type="number" class="p-input" placeholder="1" />
        </div>
        <div class="p-field">
          <label class="p-field__label">고유키 (Unique Key)</label>
          <input v-model="config.uniqueKey" class="p-input" placeholder="실제 고유키" />
          <p class="p-field__hint p-field__hint--warn">실제 고유키를 입력해주세요.</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">사용자 ID (userIdx)</label>
          <input v-model.number="userIdx" type="number" class="p-input" min="1" placeholder="1" />
          <p class="p-field__hint p-field__hint--warn">실제 사용자 ID를 입력해주세요.</p>
        </div>
      </div>
      <div class="p-field">
        <label class="p-field__label">세션 ID</label>
        <input v-model="config.sessionId" class="p-input" placeholder="session_2024_001" />
      </div>
      <div class="spk-test__start">
        <ABtn kind="yellow" icon="play_arrow" block :disabled="!canStart" @click="startConversation">
          대화 시작하기
        </ABtn>
      </div>
    </ACard>

    <!-- 채팅 인터페이스 -->
    <ACard v-if="conversationStarted" body-flush class="spk-test__chat">
      <div class="spk-test__chat-head">
        <h5 class="spk-test__sub-h">
          <i class="material-icons-round">forum</i>
          {{ conversationBlocked ? '대화 종료됨' : '대화 진행 중' }}
        </h5>
        <div class="spk-test__chat-meta">
          <APill kind="info">
            대화 턴: {{ conversationTurnCount }}{{ requestParamsJson ? '/' + maxConversationTurns : '' }}
          </APill>
          <APill
            v-if="requestParamsJson"
            :kind="abusingCount > 0 ? (abusingCount >= abusingLimit ? 'err' : 'warn') : 'default'"
          >
            {{ abusingCount > 0 ? '⚠️ ' : '' }}부적절 응답: {{ abusingCount }}/{{ abusingLimit }}
          </APill>
        </div>
      </div>

      <div ref="messagesContainer" class="spk-test__messages">
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['spk-test__msg-wrap', message.role === 'user' ? 'spk-test__msg-wrap--user' : 'spk-test__msg-wrap--ai']"
        >
          <div class="spk-test__bubble">
            <div class="spk-test__msg-head">
              <span class="spk-test__msg-role">
                <i class="material-icons-round">{{ message.role === 'user' ? 'person' : 'smart_toy' }}</i>
                {{ message.role === 'user' ? '사용자' : 'AI 튜터' }}
              </span>
              <span class="spk-test__msg-time">{{ message.timestamp }}</span>
            </div>
            <div class="spk-test__msg-content">{{ message.content }}</div>
            <div v-if="message.evaluation" class="spk-test__eval">
              <div class="spk-test__eval-h">
                <i class="material-icons-round">assessment</i>
                평가 결과
              </div>
              <div v-if="message.evaluation.rubric_scores">
                <strong>루브릭 점수:</strong>
                <div v-for="(score, idx) in message.evaluation.rubric_scores" :key="idx" class="spk-test__score">
                  <APill kind="info">{{ score.criteria }}</APill>
                  <APill kind="ok">{{ score.score }}점</APill>
                </div>
              </div>
              <div v-if="message.evaluation.checklist_results">
                <strong>체크리스트:</strong>
                <div v-for="(item, idx) in message.evaluation.checklist_results" :key="idx" class="spk-test__check">
                  <i
                    class="material-icons-round"
                    :class="item.completed ? 'spk-test__check-ok' : 'spk-test__check-no'"
                  >{{ item.completed ? 'check_circle' : 'radio_button_unchecked' }}</i>
                  {{ item.name }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="currentAiMessage" class="spk-test__msg-wrap spk-test__msg-wrap--ai">
          <div class="spk-test__bubble spk-test__bubble--typing">
            <div class="spk-test__msg-head">
              <span class="spk-test__msg-role">
                <i class="material-icons-round">smart_toy</i>
                AI 튜터 (입력 중...)
              </span>
            </div>
            <div class="spk-test__msg-content">
              {{ currentAiMessage }}
              <span class="spk-test__cursor">|</span>
            </div>
          </div>
        </div>

        <div v-if="isLoading && !currentAiMessage" class="spk-test__msg-wrap spk-test__msg-wrap--ai">
          <div class="spk-test__bubble">
            <div class="spk-test__typing-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>

        <div v-if="messages.length === 0 && !isLoading" class="spk-test__empty">
          <i class="material-icons-round">chat_bubble_outline</i>
          <p>아래 입력창에 메시지를 입력하여 대화를 시작하세요</p>
        </div>
      </div>

      <div class="spk-test__input-area">
        <div v-if="conversationBlocked" class="spk-test__alert spk-test__alert--err">
          <i class="material-icons-round">block</i>
          대화를 초기화하여 다시 시작하세요.
        </div>
        <div v-else class="spk-test__input-row">
          <input
            v-model="userInput"
            type="text"
            class="p-input"
            placeholder="메시지를 입력하세요..."
            :disabled="isLoading"
            @keyup.enter="sendMessage"
          />
          <ABtn
            kind="yellow"
            icon="send"
            icon-only
            :disabled="!userInput.trim() || isLoading"
            @click="sendMessage"
          />
        </div>
      </div>
    </ACard>

    <!-- API 로그 -->
    <ACard v-if="apiLogs.length > 0 && conversationStarted" body-flush>
      <div class="spk-test__log-head">
        <h6 class="spk-test__sub-h">
          <i class="material-icons-round">code</i>
          API 요청/응답 로그 ({{ apiLogs.length }}건)
        </h6>
        <ABtn kind="ghost" size="sm" icon="delete" @click="clearLogs">로그 삭제</ABtn>
      </div>

      <div
        v-for="(log, index) in apiLogs"
        :key="index"
        class="spk-test__log-entry"
        :class="{ 'is-error': log.error }"
      >
        <div class="spk-test__log-bar" @click="toggleLog(index)">
          <div class="spk-test__log-info">
            <APill :kind="log.error ? 'err' : 'ok'">{{ log.error ? '❌' : '✅' }} #{{ apiLogs.length - index }}</APill>
            <span class="spk-test__log-time">{{ log.timestamp }}</span>
            <span class="spk-test__log-msg">{{ log.userMessage }}</span>
          </div>
          <i class="material-icons-round">{{ log.expanded ? 'expand_less' : 'expand_more' }}</i>
        </div>
        <div v-if="log.expanded" class="spk-test__log-body">
          <h6>요청 (Request)</h6>
          <pre class="spk-test__log-pre">{{ JSON.stringify(log.request, null, 2) }}</pre>
          <h6>응답 (Response)</h6>
          <pre class="spk-test__log-pre">{{ JSON.stringify(log.response, null, 2) }}</pre>
          <div v-if="log.error">
            <h6 class="spk-test__log-err-h">에러 상세</h6>
            <pre class="spk-test__log-pre">{{ JSON.stringify(log.errorDetails, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </ACard>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";

export default {
  name: "SpontaneousSpeakingTest",
  components: { APageHead, ACard, ABtn, APill },
  data() {
    return {
      config: {
        barcode: '9781635190151',
        chapterNumber: 1,
        uniqueKey: '',
        promptKind: 'spontaneous_conversation',
        sessionId: `session_${Date.now()}`,
      },
      conversationStarted: false,
      messages: [],
      userInput: '',
      isLoading: false,
      isTestingConnection: false,
      apiLogs: [],
      requestParamsJson: null,
      abusingLimit: 3,
      maxConversationTurns: 10,
      abusingCount: 0,
      conversationTurnCount: 0,
      conversationBlocked: false,
      userIdx: 1,
      currentAiMessage: '',
    };
  },
  computed: {
    canStart() {
      return this.config.barcode.trim() &&
             this.config.chapterNumber > 0 &&
             this.config.uniqueKey.trim() &&
             this.config.sessionId.trim();
    },
  },
  beforeUnmount() {
    console.log('🔌 컴포넌트 언마운트');
  },
  methods: {
    startConversation() {
      if (!this.canStart) {
        alert('모든 필수 정보를 입력해주세요.');
        return;
      }
      this.conversationStarted = true;
    },

    async sendMessage() {
      if (!this.userInput.trim() || this.isLoading) return;
      if (this.conversationBlocked) {
        alert('부적절한 언어 사용이 3회 이상 감지되어 대화가 차단되었습니다.');
        return;
      }
      const userMessage = this.userInput.trim();
      this.userInput = '';
      await this.callConversationAPI(userMessage);
    },

    async callConversationAPI(userMessage) {
      this.isLoading = true;
      this.addMessage('user', userMessage);

      const logEntry = {
        timestamp: this.formatFullTime(new Date()),
        userMessage,
        request: null,
        response: null,
        error: false,
        errorDetails: null,
        expanded: false,
      };

      try {
        const dialogueHistory = this.messages
          .slice(0, -1)
          .filter((m) => m.role === 'user' || m.role === 'ai')
          .map((m) => ({ role: m.role, message: m.content }));

        const apiUrl = `${this.$CONF.API_URL}/ai-prompt/spontaneous-conversation-sse/${this.userIdx}/${this.config.barcode}/${this.config.chapterNumber}/${this.config.uniqueKey}`;
        const requestBody = {
          dialogueHistory,
          userMessage,
          requestParamsJson: this.requestParamsJson,
        };
        logEntry.request = { url: apiUrl, method: 'POST', body: requestBody };

        const token = localStorage.getItem('access_token');
        if (!token || token === 'null') {
          throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        }

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let data = null;
        let streamDone = false;

        const parseLine = (trimmedLine) => {
          let eventData = null;
          try {
            if (trimmedLine.startsWith('data: ')) {
              eventData = JSON.parse(trimmedLine.substring(6));
            } else if (trimmedLine.startsWith('{') || trimmedLine.startsWith('[')) {
              eventData = JSON.parse(trimmedLine);
            } else {
              const colonIndex = trimmedLine.indexOf(':');
              if (colonIndex > 0 && colonIndex < trimmedLine.length - 1) {
                eventData = JSON.parse(trimmedLine.substring(colonIndex + 1));
              }
            }
            if (eventData) {
              if (eventData.request_params_json) {
                this.requestParamsJson = eventData.request_params_json;
              }
              const outputData = eventData.results?.outputData || eventData;
              if (outputData.aiMessage) {
                this.currentAiMessage = outputData.aiMessage;
                this.$nextTick(() => this.scrollToBottom());
              }
            }
          } catch (e) {
            console.warn('⚠️ SSE 데이터 파싱 실패:', trimmedLine, e);
          }
          return eventData;
        };

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) { streamDone = true; break; }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (trimmedLine === '') continue;
            const parsedData = parseLine(trimmedLine);
            if (parsedData) data = parsedData;
          }
        }

        if (buffer.trim()) {
          const parsedData = parseLine(buffer.trim());
          if (parsedData) data = parsedData;
        }

        if (!data) {
          throw new Error('SSE 스트리밍에서 데이터를 받지 못했습니다.');
        }

        const outputData = data.results?.outputData || data;
        const aiMessage = outputData.aiMessage;

        if (aiMessage) {
          this.addMessage('ai', aiMessage);
          this.currentAiMessage = '';
        } else {
          throw new Error('AI 응답 메시지를 받지 못했습니다.');
        }

        if (data.request_params_json) {
          this.requestParamsJson = data.request_params_json;
          try {
            const requestParams = JSON.parse(data.request_params_json);
            if (requestParams.inputData?.limitInfo) {
              this.abusingLimit = requestParams.inputData.limitInfo.abusingLimit || 3;
              this.maxConversationTurns = requestParams.inputData.limitInfo.maxConversationTurns || 10;
            }
          } catch (parseError) {
            console.warn('⚠️ limitInfo 파싱 실패:', parseError);
          }
        }

        this.conversationTurnCount++;

        const scopeCheckResult = outputData.scopeCheckResult;
        if (scopeCheckResult?.isOutOfScope) {
          console.warn('⚠️ 학습 범위 벗어남:', scopeCheckResult.reason);
        }

        const abusingCheckResult = outputData.abusingCheckResult;
        const isAbusing = !!(abusingCheckResult && abusingCheckResult.isAbusing);
        if (isAbusing) {
          this.abusingCount++;
        }

        let blockedReason = null;
        if (this.conversationTurnCount >= this.maxConversationTurns) {
          blockedReason = 'max_turns_reached';
        }

        if (blockedReason) {
          this.conversationBlocked = true;
          if (blockedReason === 'max_turns_reached') {
            alert(`✅ 대화가 완료되었습니다\n\n최대 대화 횟수(${this.maxConversationTurns}회)에 도달했습니다.`);
          }
          await this.callEvaluationAPI(blockedReason);
        }

        logEntry.response = data;
        this.isLoading = false;
      } catch (error) {
        logEntry.error = true;
        logEntry.errorDetails = {
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
        };
        logEntry.response = { error: error.message };

        console.error('❌ SSE API 호출 오류:', error);
        let errorMessage = '오류가 발생했습니다.';
        if (error.message.includes('인증')) {
          errorMessage = '인증 토큰이 만료되었거나 유효하지 않습니다. 다시 로그인해주세요.';
        } else if (error.message.includes('HTTP error')) {
          const statusMatch = error.message.match(/status: (\d+)/);
          const status = statusMatch ? statusMatch[1] : 'unknown';
          if (status === '400') errorMessage = '잘못된 요청입니다. 입력 데이터를 확인해주세요.';
          else if (status === '401') errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
          else if (status === '403') errorMessage = '접근 권한이 없습니다.';
          else if (status === '404') errorMessage = 'API 엔드포인트를 찾을 수 없습니다.';
          else if (status === '500') errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
          else errorMessage = `서버 오류 (${status}): 잠시 후 다시 시도해주세요.`;
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = '네트워크 연결을 확인해주세요.';
        } else if (error.message.includes('timeout')) {
          errorMessage = '요청 시간이 초과되었습니다. 다시 시도해주세요.';
        } else if (error.message.includes('SSE')) {
          errorMessage = 'SSE 스트리밍 처리 중 오류가 발생했습니다.';
        }

        this.addMessage('ai', `${errorMessage}\n\n상세: ${error.message}`);
        this.currentAiMessage = '';
      } finally {
        this.apiLogs.unshift(logEntry);
        this.isLoading = false;
      }
    },

    async callEvaluationAPI(reason) {
      try {
        const dialogueHistory = this.messages
          .filter((m) => m.role === 'user' || m.role === 'ai')
          .map((m) => ({ role: m.role, message: m.content }));

        const evaluationUrl = `/ai-prompt/evaluation-spontaneous-conversation/${this.userIdx}/${this.config.barcode}/${this.config.chapterNumber}/${this.config.uniqueKey}`;
        const lastUserMessage = this.messages.filter((m) => m.role === 'user').pop()?.content || '';
        const requestBody = {
          dialogueHistory,
          userMessage: lastUserMessage,
          requestParamsJson: this.requestParamsJson,
          abusingCount: this.abusingCount || 0,
        };

        const response = await this.$axios({
          method: 'post',
          url: evaluationUrl,
          data: requestBody,
          loadingBar: true,
        });

        if (response.data.success) {
          const evaluation = response.data.evaluation_result;
          if (evaluation) {
            let resultMessage = '대화 평가가 완료되었습니다!\n\n';
            if (evaluation.overall_score !== undefined) resultMessage += `종합 점수: ${evaluation.overall_score}\n`;
            if (evaluation.feedback) resultMessage += `피드백: ${evaluation.feedback}\n`;
            alert(resultMessage);
          } else {
            alert('평가가 완료되었지만 결과를 표시할 수 없습니다.');
          }
        } else {
          alert(response.data.message || '평가 처리 중 문제가 발생했습니다.');
        }
      } catch (error) {
        console.error('❌ 평가 API 호출 오류:', error);
        alert('대화 평가 중 오류가 발생했습니다.');
      }
    },

    addMessage(role, content, evaluation = null) {
      this.messages.push({
        role,
        content,
        timestamp: this.formatTime(new Date()),
        evaluation,
      });
      this.$nextTick(() => this.scrollToBottom());
    },

    formatTime(date) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },

    formatFullTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) container.scrollTop = container.scrollHeight;
    },

    resetConversation() {
      if (confirm('대화를 초기화하시겠습니까?')) {
        this.conversationStarted = false;
        this.messages = [];
        this.userInput = '';
        this.apiLogs = [];
        this.requestParamsJson = null;
        this.abusingCount = 0;
        this.conversationTurnCount = 0;
        this.conversationBlocked = false;
        this.currentAiMessage = '';
        this.abusingLimit = 3;
        this.maxConversationTurns = 10;
        this.config.sessionId = `session_${Date.now()}`;
      }
    },

    toggleLog(index) {
      this.apiLogs[index].expanded = !this.apiLogs[index].expanded;
    },

    clearLogs() {
      if (confirm('모든 API 로그를 삭제하시겠습니까?')) {
        this.apiLogs = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.spk-test {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__sub-h {
    margin: 0 0 14px;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    > i { font-size: 18px; color: $p-point-deep; }
  }

  &__start {
    margin-top: 16px;
  }

  // ── 채팅 ──
  &__chat {
    display: flex;
    flex-direction: column;
    height: 640px;
  }

  &__chat-head {
    padding: 14px 18px;
    border-bottom: 1px solid $p-line;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    h5 { margin: 0; }
  }

  &__chat-meta {
    display: inline-flex;
    gap: 6px;
  }

  &__messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: $p-bg;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__msg-wrap {
    display: flex;

    &--user { justify-content: flex-end; }
    &--ai { justify-content: flex-start; }
  }

  &__bubble {
    max-width: 70%;
    padding: 12px 14px;
    border-radius: $p-radius-lg;
    box-shadow: $p-shadow-sm;
    background: $p-panel;
    border: 1px solid $p-line;

    .spk-test__msg-wrap--user & {
      background: $p-ink;
      color: $p-point;
      border-color: $p-ink;
      border-bottom-right-radius: 4px;
    }

    .spk-test__msg-wrap--ai & {
      border-bottom-left-radius: 4px;
    }

    &--typing {
      background: $p-point-soft !important;
      border-color: $p-point-deep !important;
    }
  }

  &__msg-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    font-size: 11px;
    opacity: 0.85;
  }

  &__msg-role {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 700;

    > i { font-size: 14px !important; }
  }

  &__msg-time {
    font-family: $p-font-mono;
    font-size: 10px;
  }

  &__msg-content {
    font-size: 13px;
    line-height: 1.55;
    word-wrap: break-word;
    white-space: pre-wrap;
  }

  &__cursor {
    display: inline-block;
    margin-left: 2px;
    font-weight: bold;
    color: $p-point-deep;
    animation: spk-blink 1s infinite;
  }

  &__eval {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    font-size: 12px;

    .spk-test__msg-wrap--user & {
      border-top-color: rgba(255, 255, 255, 0.2);
    }
  }

  &__eval-h {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 800;
    margin-bottom: 6px;

    > i { font-size: 14px !important; }
  }

  &__score, &__check {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0;
  }

  &__check-ok { color: $p-ok; }
  &__check-no { color: $p-ink-soft; }

  &__typing-dots {
    display: flex;
    gap: 4px;
    padding: 6px 0;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: $p-point-deep;
      animation: spk-typing 1.4s infinite;

      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
    }
  }

  &__empty {
    text-align: center;
    padding: 60px 24px;
    color: $p-ink-soft;

    > i {
      font-size: 48px;
      display: inline-block;
      margin-bottom: 12px;
    }

    p { margin: 0; font-size: 13px; }
  }

  &__input-area {
    border-top: 1px solid $p-line;
    padding: 14px 18px;
    background: $p-panel;
  }

  &__input-row {
    display: flex;
    gap: 8px;

    > .p-input { flex: 1; }
    > .a-btn { width: 40px; height: 38px; }
  }

  &__alert {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    border-radius: $p-radius-md;
    font-size: 12px;

    > i { font-size: 18px; }

    &--err { background: $p-err-soft; border-left: 3px solid $p-err; color: $p-err; }
  }

  // ── API 로그 ──
  &__log-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid $p-line;
  }

  &__log-entry {
    border-bottom: 1px solid $p-line-soft;

    &.is-error {
      background: lighten($p-err-soft, 4%);
    }
  }

  &__log-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 18px;
    cursor: pointer;

    &:hover { background: $p-panel-alt; }
  }

  &__log-info {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    flex: 1;
    min-width: 0;
  }

  &__log-time {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__log-msg {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $p-ink-mid;
  }

  &__log-body {
    padding: 12px 18px 14px;
    background: $p-panel-alt;

    h6 {
      margin: 8px 0 4px;
      font-size: 11px;
      font-weight: 800;
      color: $p-ink-mid;
    }
  }

  &__log-err-h {
    color: $p-err !important;
  }

  &__log-pre {
    margin: 0 0 8px;
    padding: 10px 12px;
    background: $p-ink;
    color: $p-point;
    border-radius: $p-radius-md;
    font-family: $p-font-mono;
    font-size: 10px;
    line-height: 1.5;
    max-height: 200px;
    overflow-y: auto;
    white-space: pre-wrap;
  }
}

@keyframes spk-typing {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30% { transform: translateY(-8px); opacity: 1; }
}

@keyframes spk-blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}
</style>
